import React, { useEffect, useState, useRef } from 'react'
import styled, { keyframes } from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { getRecentWhatsNewRoutine } from './ducks/actions'
import { selectWhatsNewRecent } from './ducks/selectors'
import { ReactComponent as CloseIcon } from 'assets/images/close.svg'
import NewsDetailModal from './NewsDetailModal'
import { isNewItem } from 'utils/isNewItem'
import SearchIcon from "@mui/icons-material/Search";

const WhatsNewPanel = ({ onClose }) => {
  const dispatch = useDispatch()
  const recent = useSelector(selectWhatsNewRecent) || []
  const [sidebarWidth, setSidebarWidth] = useState(76)
  const [selectedNews, setSelectedNews] = useState(null)
  const panelRef = useRef(null)
  const [seenNews, setSeenNews] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    setLoading(true)
    setError(null)
    dispatch(getRecentWhatsNewRoutine())
  }, [dispatch])

  useEffect(() => {
    if (recent.length > 0) {
      setLoading(false)
    }
  }, [recent])


  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose?.()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onClose])

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem('seen_whatsnew_ids') || '[]')
    setSeenNews(stored)
  }, [])

  useEffect(() => {
    if (!recent.length) return

    const unseen = recent.filter(
      (i) => isNewItem(i, 'whatsnew') && !seenNews.includes(i.id || i.uuid)
    )

    if (unseen.length === 0) {
      localStorage.setItem('last_seen_whatsnew', new Date().toISOString())
    }
  }, [recent, seenNews])

  const handleOpenItem = (item) => {
    setSelectedNews(item)

    const id = item.id || item.uuid
    if (!id) return

    const seenIds = JSON.parse(localStorage.getItem('seen_whatsnew_ids') || '[]')

    if (!seenIds.includes(id)) {
      const updated = Array.from(new Set([...seenIds, id]))
      localStorage.setItem('seen_whatsnew_ids', JSON.stringify(updated))
      setSeenNews(updated)
    }

    const updatedSeen = JSON.parse(localStorage.getItem('seen_whatsnew_ids') || '[]')

    const hasUnseenNew = recent.some(
      (n) => isNewItem(n, 'whatsnew') && !updatedSeen.includes(n.id || n.uuid)
    )

    if (!hasUnseenNew && recent.length > 0) {
      localStorage.setItem('last_seen_whatsnew', new Date().toISOString())
    }

    window.dispatchEvent(
      new CustomEvent('whatsnew-updated', {
        detail: { hasUnseenNew },
      })
    )

    dispatch(getRecentWhatsNewRoutine())
  }

  const filteredNews = recent.filter((item) => {
    const term = searchTerm.toLowerCase()
    return (
      item.title?.toLowerCase().includes(term) ||
      item.description?.toLowerCase().includes(term)
    )
  })

  return (
    <PanelWrapper style={{ left: `${sidebarWidth}px` }} ref={panelRef}>
      <Header>
        <Title>What’s New</Title>
        <CloseButton onClick={onClose}>
          <CloseIcon />
        </CloseButton>
      </Header>

      <SearchBox>
        <SearchIconStyled />
        <input
          type="text"
          placeholder="Search updates..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </SearchBox>

      {loading ? (
        <SkeletonList>
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonCard key={i}>
              <SkeletonImage />
              <SkeletonTitle />
              <SkeletonText />
            </SkeletonCard>
          ))}
        </SkeletonList>
      ) : error ? (
        <ErrorState>{error}</ErrorState>
      ) : (
        <List>
          {filteredNews.map((item, i) => (
            <Card key={i} onClick={() => handleOpenItem(item)}>
              {item.image && <PreviewImage src={item.image} alt={item.title} />}
              <h4>
                {item.title}
                {isNewItem(item, 'whatsnew') && !seenNews.includes(item.id || item.uuid) && (
                  <NewBadge>NEW</NewBadge>
                )}
              </h4>
              {item.description && (
                <p>
                  {item.description.length > 120
                    ? item.description.substring(0, 120) + '...'
                    : item.description}
                </p>
              )}
            </Card>
          ))}

          {!loading && filteredNews.length === 0 && (
            <EmptyState>No results found for “{searchTerm}”.</EmptyState>
          )}
        </List>
      )}

      <Footer>
        <button
          onClick={() => {
            onClose()
            setTimeout(() => {
              window.dispatchEvent(new Event('openTutorialsPanelFromWhatsNew'))
            }, 150)
          }}
        ></button>
      </Footer>

      {selectedNews && (
        <NewsDetailModal
          item={selectedNews}
          onClose={() => setSelectedNews(null)}
        />
      )}
    </PanelWrapper>
  )
}

export default WhatsNewPanel

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`

const SkeletonList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  margin-top: 10px;
`

const SkeletonCard = styled.div`
  background: #fff;
  border-radius: 14px;
  padding: 16px 18px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.05);
`

const SkeletonBlock = styled.div`
  background: linear-gradient(90deg, #f1f3f6 25%, #e9ecf2 37%, #f1f3f6 63%);
  background-size: 400% 100%;
  border-radius: 6px;
  animation: ${shimmer} 1.6s infinite linear;
`

const SkeletonImage = styled(SkeletonBlock)`
  height: 120px;
  border-radius: 10px;
  margin-bottom: 14px;
`

const SkeletonTitle = styled(SkeletonBlock)`
  width: 70%;
  height: 16px;
  margin-bottom: 8px;
`

const SkeletonText = styled(SkeletonBlock)`
  width: 90%;
  height: 12px;
  margin-bottom: 6px;
`

const ErrorState = styled.div`
  text-align: center;
  color: #d14724;
  font-weight: 600;
  font-size: 15px;
  padding: 40px 0;
`

const PanelWrapper = styled.div`
  position: fixed;
  top: 0;
  width: 460px;
  bottom: 0;
  height: auto;

  background: #fafbfc;
  display: flex;
  flex-direction: column;
  padding: 0 30px 25px;
  box-sizing: border-box;
  overflow-y: auto;
  box-shadow: 6px 0 20px rgba(0, 0, 0, 0.1);
  border-top-right-radius: 18px;
  border-bottom-right-radius: 18px;
  z-index: 9999;
  -webkit-overflow-scrolling: touch;
  animation: slideIn 0.3s ease;

  @keyframes slideIn {
    from { transform: translateX(-100%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100vw;
    left: 0 !important;
    border-radius: 0;
    padding: 0 16px 20px;
    box-shadow: none;

    top: 0;
    bottom: 0;
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: transparent;
  padding: 24px 0 20px;
  top: 0;
  z-index: 10;
`

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  color: #111;

  @media (max-width: 480px) {
    font-size: 20px;
  }
`

const CloseButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    width: 23px;
    height: 23px;
    stroke-width: 2;
  }
  &:hover svg {
    opacity: 0.8;
  }
`

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  margin-top: 10px;
`

const Card = styled.div`
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.05);
  padding: 16px 18px;
  cursor: pointer;
  transition: all 0.2s ease;

  @media (max-width: 480px) {
    padding: 14px;
  }

  &:hover {
    transform: translateY(-2px);
    background: #f8f9fb;
  }

  h4 {
    margin: 0 0 8px;
    font-size: 17px;
    font-weight: 700;
    line-height: 1.4;
  }

  p {
    font-size: 14px;
    color: #555;
    margin: 0;
    line-height: 1.5;
  }
`

const PreviewImage = styled.img`
  width: 100%;
  max-height: 180px;
  object-fit: cover;
  border-radius: 10px;
  margin-bottom: 12px;
`

const Footer = styled.div`
  margin-top: auto;
  text-align: right;
  padding-top: 20px;
  button {
    background: none;
    border: none;
    color: #007bff;
    cursor: pointer;
    font-weight: 600;
    &:hover {
      text-decoration: underline;
    }
  }
`

const NewBadge = styled.span`
  background: #ff4d4f;
  color: white;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 10px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-left: 8px;
`

const EmptyState = styled.div`
  text-align: center;
  color: #999;
  font-size: 14px;
  padding: 30px 0;
`

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  border: 1px solid #e4e4e4;
  border-radius: 10px;
  padding: 8px 14px;
  margin-bottom: 18px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.03);

  input {
    flex: 1;
    border: none;
    outline: none;
    font-size: 14px;
    background: transparent;
    color: #333;

    &::placeholder {
      color: #aaa;
    }
  }
`

const SearchIconStyled = styled(SearchIcon)`
  width: 18px;
  height: 18px;
  opacity: 0.6;
`
